import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mic, Smartphone, Zap, Trophy, StarIcon } from 'lucide-react';
import { Badge, SectionHeading, PhoneMockup, TestimonialCard } from '../components/ui';
import { CharacterCard, Character } from '../components/CharacterCard';
import { PricingCard, PricingPlan } from '../components/PricingCard';
import { HilariousMeter } from '../components/HilariousMeter';
const CHARACTERS: Character[] = [{
  id: 'vinny',
  name: 'Vinny Capuano',
  hometown: 'Brooklyn, NY',
  color: 'bg-blue-900',
  borderColorClass: 'border-blue-500',
  quote: "Whaddya think you're doin' startin' Deebo on a BYE week? You stunad."
}, {
  id: 'dale',
  name: 'Dale & Earl',
  hometown: 'Harlan County, KY',
  color: 'bg-green-900',
  borderColorClass: 'border-green-600',
  quote: "Well I'll be dadgum... you started WHO? Boy, even the mule woulda known better."
}, {
  id: 'marisol',
  name: 'Marisol Cruz',
  hometown: 'San Juan, PR',
  color: 'bg-rose-900',
  borderColorClass: 'border-rose-500',
  quote: 'Ay, mira — Josh Allen casi me da un ataque al corazón. Casi. CASI, David!'
}, {
  id: 'giuseppe',
  name: 'Giuseppe Conti',
  hometown: 'Palermo, Sicily',
  color: 'bg-red-950',
  borderColorClass: 'border-red-800',
  quote: '...You benched Jefferson. (long pause) ...I have nothing more to say to you.'
}, {
  id: 'daniel',
  name: 'Daniel Ashworth',
  hometown: 'London, UK',
  color: 'bg-slate-800',
  borderColorClass: 'border-slate-400',
  quote: "I've watched 40 years of rugby. Nothing prepared me for this level of suffering."
}, {
  id: 'gary',
  name: "G'Day Gary",
  hometown: 'Melbourne, AU',
  color: 'bg-orange-900',
  borderColorClass: 'border-orange-500',
  quote: 'STREWTH mate, that was more painful than a box jellyfish to the face! RIPPER matchup!'
}, {
  id: 'ricky',
  name: 'Ricky Rotardo',
  hometown: 'Parts Unknown',
  color: 'bg-yellow-900',
  borderColorClass: 'border-yellow-400',
  quote: "Every week I watch you self-destruct and every week I'm NEVER disappointed."
}, {
  id: 'jorge',
  name: 'Jorge (Interrupt)',
  hometown: 'Mexico City, MX',
  color: 'bg-red-600',
  borderColorClass: 'border-red-500',
  quote: '—¡NO MAMES! ¡TOUCHDOWN! ¡TOUCHED—',
  isInterrupt: true
}];
const PRICING: PricingPlan[] = [{
  id: 'draft',
  title: 'Draft Day Rental',
  price: '$19.99',
  period: '',
  badge: 'TRY IT TONIGHT',
  badgeColor: 'lime',
  borderClass: 'border-lime',
  glowClass: 'hover:shadow-[0_0_30px_rgba(200,255,0,0.2)]',
  description: 'One-night access. Perfect for draft night. Zero commitment.',
  ctaText: 'RENT FOR TONIGHT',
  ctaClass: 'bg-transparent border-2 border-lime text-lime hover:bg-lime hover:text-charcoal'
}, {
  id: 'starter',
  title: 'Starter',
  price: '$49',
  period: 'season',
  borderClass: 'border-white/20',
  glowClass: 'hover:border-white/50',
  description: '1 league. 2 commentators. Tiers 1-2.',
  ctaText: 'START YOUR SEASON',
  ctaClass: 'bg-white text-charcoal hover:bg-slate-200'
}, {
  id: 'playoff',
  title: 'Playoff Push',
  price: '$99',
  period: 'season',
  badge: '⭐ MOST POPULAR',
  badgeColor: 'orange',
  borderClass: 'border-orange',
  glowClass: 'shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] scale-105 z-10',
  description: '1 league. Full character cast. Tiers 1-3 unlocked.',
  ctaText: 'GO PLAYOFF PUSH',
  ctaClass: 'bg-orange text-white hover:bg-orange/80',
  popular: true
}, {
  id: 'afterdark',
  title: 'After Dark',
  price: '$149',
  period: 'season',
  badge: '🔥 ADULTS ONLY',
  badgeColor: 'crimson',
  borderClass: 'border-crimson',
  glowClass: 'hover:shadow-[0_0_30px_rgba(220,38,38,0.2)]',
  description: '1 league. Full cast. All 4 tiers. Clone upsell included.',
  ctaText: 'UNLOCK AFTER DARK',
  ctaClass: 'bg-crimson text-white hover:bg-crimson/80'
}, {
  id: 'commissioner',
  title: 'Commissioner Pack',
  price: '$199',
  period: 'season',
  badge: '👑 BEST VALUE',
  badgeColor: 'gold',
  borderClass: 'border-gold',
  glowClass: 'hover:shadow-[0_0_30px_rgba(245,197,24,0.2)]',
  description: '3 leagues. Every feature. Every tier. Commissioner goes FREE when 8+ members pay.',
  subNote: 'Loser Pays mechanic available — collect upfront, auto-charge last place at season end.',
  ctaText: 'GET COMMISSIONER PACK',
  ctaClass: 'bg-gold text-charcoal hover:bg-yellow-400'
}];
export const HomePage = () => {
  return <div className="w-full" data-id="element-195">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-stadium-grain bg-cover bg-center" data-id="element-196">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/80 to-charcoal" data-id="element-197"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-198">
          <div className="max-w-5xl mx-auto text-center" data-id="element-199">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-200">
              <div className="inline-flex items-center gap-2 bg-dark/80 backdrop-blur border border-white/10 rounded-full px-4 py-2 mb-8" data-id="element-201">
                <Trophy size={16} className="text-gold" data-id="element-202" />
                <span className="text-sm font-bold uppercase tracking-wider text-gold" data-id="element-203">
                  Commissioner Pack — 3 Leagues Included
                </span>
              </div>

              <h1 className="text-7xl md:text-[140px] leading-[0.85] font-black font-heading uppercase -skew-x-6 mb-8 text-shadow-broadcast" data-id="element-204">
                <span className="block text-white" data-id="element-205">YOUR LEAGUE.</span>
                <span className="block text-lime" data-id="element-206">OUR ROAST.</span>
                <span className="block text-white" data-id="element-207">ALL SEASON.</span>
              </h1>

              <p className="text-xl md:text-3xl text-slate font-medium max-w-3xl mx-auto mb-12 leading-tight" data-id="element-208">
                AI commentary so savage your league group chat will never be the
                same. Audio clips + texts delivered after every game.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12" data-id="element-209">
                <Link to="/pricing" className="w-full sm:w-auto bg-lime text-charcoal px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:scale-105 transition-all shadow-[0_0_30px_rgba(200,255,0,0.4)]" data-id="element-210">
                  Get Started — $19.99 Draft Day
                </Link>
                <button className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:text-charcoal transition-all" data-id="element-211">
                  Hear A Sample
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 text-slate" data-id="element-212">
                <div className="flex text-gold" data-id="element-213">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} size={16} className="fill-current" data-id="element-214" />)}
                </div>
                <span className="text-sm font-bold" data-id="element-215">
                  "My league is obsessed" — Commissioner, 12-team PPR
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HILARIOUS METER SECTION */}
      <section className="py-32 bg-charcoal relative border-t border-white/5" data-id="element-216">
        <div className="container mx-auto px-4" data-id="element-217">
          <SectionHeading title="PICK YOUR LEVEL. PICK YOUR CHAOS." className="text-center" data-id="element-218" />
          <HilariousMeter data-id="element-219" />
        </div>
      </section>

      {/* CHARACTER SHOWCASE */}
      <section className="py-32 bg-dark relative overflow-hidden border-y border-white/5" data-id="element-220">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-lime/50 to-transparent" data-id="element-221"></div>

        <div className="container mx-auto px-4" data-id="element-222">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6" data-id="element-223">
            <SectionHeading title="MEET THE CAST" subtitle="11 characters. 11 accents. Zero mercy." className="mb-0" data-id="element-224" />
            <Link to="/characters" className="inline-flex items-center gap-2 text-lime font-bold uppercase tracking-wider hover:text-white transition-colors" data-id="element-225">
              See The Full Cast <Zap size={16} className="fill-current" data-id="element-226" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-227">
            {CHARACTERS.map(char => <CharacterCard key={char.id} character={char} data-id="element-228" />)}
          </div>

          <div className="mt-16 text-center md:hidden" data-id="element-229">
            <Link to="/characters" className="bg-white text-charcoal px-8 py-4 rounded-xl font-black font-heading text-xl uppercase tracking-wide inline-block" data-id="element-230">
              See The Full Cast
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-32 bg-charcoal" data-id="element-231">
        <div className="container mx-auto px-4" data-id="element-232">
          <SectionHeading title="HOW IT WORKS" className="text-center mb-20" data-id="element-233" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-234">
            <div className="space-y-16" data-id="element-235">
              <div className="flex gap-6" data-id="element-236">
                <div className="w-16 h-16 rounded-2xl bg-lime/10 flex items-center justify-center shrink-0 border border-lime/30" data-id="element-237">
                  <Zap className="text-lime fill-lime" size={32} data-id="element-238" />
                </div>
                <div data-id="element-239">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-240">
                    1. Connect Your League
                  </h3>
                  <p className="text-slate text-lg" data-id="element-241">
                    Link your Sleeper league in 60 seconds. We pull your roster,
                    your scores, your rivals.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-242">
                <div className="w-16 h-16 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 border border-orange/30" data-id="element-243">
                  <Mic className="text-orange" size={32} data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-246">
                    2. Pick Your Cast & Chaos
                  </h3>
                  <p className="text-slate text-lg" data-id="element-247">
                    Choose 2-11 characters. Set your Hilarious Meter. We handle
                    everything else.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-248">
                <div className="w-16 h-16 rounded-2xl bg-crimson/10 flex items-center justify-center shrink-0 border border-crimson/30" data-id="element-249">
                  <Smartphone className="text-crimson" size={32} data-id="element-250" />
                </div>
                <div data-id="element-251">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-252">
                    3. Get Roasted
                  </h3>
                  <p className="text-slate text-lg" data-id="element-253">
                    Sunday 4pm hits. Audio clips and texts land. Your league
                    chat explodes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center" data-id="element-254">
              <PhoneMockup sender="VINNY CAPUANO" message="🏈 Hinge Hunters lost by 30. Josh Allen scored 23 points... for the OTHER team. Sleep well." data-id="element-255" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32 bg-dark border-y border-white/5" data-id="element-256">
        <div className="container mx-auto px-4" data-id="element-257">
          <SectionHeading title="WHAT COMMISSIONERS ARE SAYING" className="text-center mb-16" data-id="element-258" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-259">
            <TestimonialCard quote="My league threatened to quit if I don't renew next season. Mission accomplished." author="Dave S." leagueType="10-team Half-PPR" data-id="element-260" />
            <TestimonialCard quote="We have a 'Ricky Rotardo of the Week' trophy now. This is what fantasy was missing." author="Marcus T." leagueType="14-team Dynasty" data-id="element-261" />
            <TestimonialCard quote="School Spirit Mode for our PTA league was perfect. Even the kids are invested now." author="Sarah L." leagueType="8-team School League" data-id="element-262" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-32 bg-charcoal relative" data-id="element-263">
        <div className="container mx-auto px-4" data-id="element-264">
          <SectionHeading title="PICK YOUR SEASON PASS" subtitle="No monthly fees. One payment covers the entire season." className="text-center mb-20" data-id="element-265" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 items-stretch" data-id="element-266">
            {PRICING.map(plan => <PricingCard key={plan.id} plan={plan} data-id="element-267" />)}
          </div>

          <div className="mt-12 text-center" data-id="element-268">
            <p className="text-slate mb-2" data-id="element-269">
              🎓 School leagues & fundraising orgs — contact for custom quote
            </p>
            <p className="text-xs text-slate/50" data-id="element-270">
              Seasonal pricing. Covers your full fantasy season. No auto-renew
              surprises.
            </p>
          </div>
        </div>
      </section>

      {/* COMMISSIONER BANNER */}
      <section className="py-20 bg-lime text-charcoal relative overflow-hidden" data-id="element-271">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" data-id="element-272"></div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-273">
          <h2 className="text-6xl md:text-8xl font-black font-heading uppercase -skew-x-6 mb-6 tracking-tighter" data-id="element-274">
            COMMISSIONER GOES FREE
          </h2>
          <p className="text-2xl font-bold max-w-2xl mx-auto mb-10" data-id="element-275">
            Get 8 league members to pay and your season is on us. Share your
            link. Collect your glory.
          </p>
          <button className="bg-charcoal text-lime px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-dark hover:scale-105 transition-all shadow-2xl" data-id="element-276">
            Start A Commissioner Pack
          </button>
        </div>
      </section>

      {/* SCHOOL SPIRIT */}
      <section id="school-spirit" className="py-32 bg-slate-900 border-t border-white/10" data-id="element-277">
        <div className="container mx-auto px-4 max-w-5xl text-center" data-id="element-278">
          <h2 className="text-5xl md:text-7xl font-black font-heading uppercase -skew-x-6 mb-8 text-blue-400" data-id="element-279">
            SCHOOL SPIRIT MODE 🎓
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-3xl mx-auto" data-id="element-280">
            Run a fantasy league for your school, team, or organization. School
            Spirit Mode locks the humor to family-safe Tier 1. Every affiliate
            link raises money for your program.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto mb-12" data-id="element-281">
            {['Dedicated fundraiser page at ITSONfantasy.com/support/[schoolname]', 'Parent referral mechanic — refer a family, get next season free', 'Custom org affiliate rates available', '100% family-safe commentary — school board approved'].map((feature, i) => <div key={i} className="flex items-start gap-4 bg-slate-800/50 p-6 rounded-2xl border border-slate-700" data-id="element-282">
                <div className="bg-blue-500/20 p-2 rounded-full text-blue-400 shrink-0" data-id="element-283">
                  <Zap size={20} className="fill-current" data-id="element-284" />
                </div>
                <p className="text-slate-200 font-medium" data-id="element-285">{feature}</p>
              </div>)}
          </div>

          <button className="bg-blue-500 text-white px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-blue-600 transition-all" data-id="element-286">
            Set Up Your School League
          </button>
        </div>
      </section>
    </div>;
};